import { useState } from 'react';
import {
  Plus, Pencil, Trash2, QrCode, RefreshCw, Loader2, AlertCircle,
} from 'lucide-react';
import { useBankAccounts, useBankAccountMutations, useReceiptSequences, useReceiptSequenceMutations } from '../hooks/useBankAccounts';
import { BankAccountForm } from '../components/BankAccountForm';
import type { BankAccount, BankAccountFormData } from '../types';

type Tab = 'bank-accounts' | 'receipt-sequences';

export function SettingsPage() {
  const [tab, setTab] = useState<Tab>('bank-accounts');

  return (
    <div className="p-6">
      <h1 className="mb-6 text-2xl font-bold">Configuración</h1>

      <div className="mb-6 flex w-fit gap-1 rounded-lg border border-border bg-muted/30 p-1">
        {([
          ['bank-accounts', 'Cuentas Bancarias'],
          ['receipt-sequences', 'Numeración de Recibos'],
        ] as [Tab, string][]).map(([key, label]) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={`rounded-md px-4 py-2 text-sm font-medium transition-all ${
              tab === key
                ? 'bg-card text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === 'bank-accounts' && <BankAccountsTab />}
      {tab === 'receipt-sequences' && <ReceiptSequencesTab />}
    </div>
  );
}

function BankAccountsTab() {
  const { data: accounts = [], isLoading, error } = useBankAccounts();
  const { create, update, remove } = useBankAccountMutations();
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<BankAccount | null>(null);
  const [qrModal, setQrModal] = useState<BankAccount | null>(null);

  const handleSave = async (form: BankAccountFormData) => {
    if (editing) {
      await update.mutateAsync({ id: editing.id, form });
    } else {
      await create.mutateAsync(form);
    }
  };

  const handleDelete = async (account: BankAccount) => {
    if (!confirm(`¿Eliminar la cuenta "${account.account_name}"?`)) return;
    await remove.mutateAsync(account.id);
  };

  if (isLoading) return <div className="flex justify-center py-12"><Loader2 className="size-6 animate-spin text-muted-foreground" /></div>;
  if (error) return <p className="text-destructive">Error al cargar cuentas bancarias.</p>;

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          Cuentas bancarias oficiales para recibir diezmos. El código QR puede ser escaneado por autoridades para realizar depósitos.
        </p>
        <button
          onClick={() => { setEditing(null); setShowForm(true); }}
          className="flex items-center gap-2 rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
        >
          <Plus className="size-4" />
          Nueva cuenta
        </button>
      </div>

      {accounts.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-border py-16 text-muted-foreground">
          <AlertCircle className="mb-2 size-8" />
          <p>No hay cuentas bancarias registradas.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {accounts.map((account) => (
            <div key={account.id} className="flex items-center gap-4 rounded-lg border border-border bg-card p-4">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <p className="truncate font-medium">{account.account_name}</p>
                  <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                    account.is_active
                      ? 'bg-success/15 text-success'
                      : 'bg-muted text-muted-foreground'
                  }`}>
                    {account.is_active ? 'Activa' : 'Inactiva'}
                  </span>
                  <span className="rounded bg-muted px-1.5 py-0.5 text-xs text-muted-foreground">
                    {account.currency}
                  </span>
                </div>
                <p className="text-sm text-muted-foreground">{account.bank_name}</p>
                <p className="font-mono text-xs text-muted-foreground">{account.account_number}</p>
                {account.account_type && (
                  <p className="text-xs text-muted-foreground">{account.account_type}</p>
                )}
              </div>

              <div className="flex items-center gap-2">
                {account.qr_code_url && (
                  <button
                    onClick={() => setQrModal(account)}
                    className="rounded-md p-2 text-muted-foreground hover:bg-muted hover:text-foreground"
                    title="Ver QR"
                  >
                    <QrCode className="size-4" />
                  </button>
                )}
                <button
                  onClick={() => { setEditing(account); setShowForm(true); }}
                  className="rounded-md p-2 text-muted-foreground hover:bg-muted hover:text-foreground"
                >
                  <Pencil className="size-4" />
                </button>
                <button
                  onClick={() => handleDelete(account)}
                  disabled={remove.isPending}
                  className="rounded-md p-2 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                >
                  <Trash2 className="size-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {showForm && (
        <BankAccountForm
          existing={editing}
          onSave={handleSave}
          onClose={() => { setShowForm(false); setEditing(null); }}
        />
      )}

      {qrModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setQrModal(null)}>
          <div className="rounded-lg border border-border bg-card p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <p className="mb-3 font-semibold">{qrModal.account_name}</p>
            <p className="mb-4 text-sm text-muted-foreground">{qrModal.bank_name} — {qrModal.account_number}</p>
            <img src={qrModal.qr_code_url!} alt="QR" className="size-64 object-contain" />
            <p className="mt-3 text-center text-xs text-muted-foreground">Toca fuera para cerrar</p>
          </div>
        </div>
      )}
    </div>
  );
}

function ReceiptSequencesTab() {
  const { data: sequences = [], isLoading } = useReceiptSequences();
  const { reset } = useReceiptSequenceMutations();
  const [editing, setEditing] = useState<{ year: number; value: string } | null>(null);

  const handleReset = async () => {
    if (!editing) return;
    const num = parseInt(editing.value, 10);
    if (isNaN(num) || num < 0) return;
    await reset.mutateAsync({ year: editing.year, lastNumber: num });
    setEditing(null);
  };

  const currentYear = new Date().getFullYear();
  const displaySequences = sequences.length > 0
    ? sequences
    : [{ year: currentYear, last_number: 0, updated_at: new Date().toISOString() }];

  if (isLoading) return <div className="flex justify-center py-12"><Loader2 className="size-6 animate-spin text-muted-foreground" /></div>;

  return (
    <div>
      <p className="mb-4 text-sm text-muted-foreground">
        Controla la numeración de recibos por año. El siguiente recibo emitido usará el número siguiente al mostrado.
        Puedes resetear a 0 para reiniciar la numeración en un nuevo año.
      </p>

      <div className="space-y-3">
        {displaySequences.map((seq) => (
          <div key={seq.year} className="flex items-center gap-4 rounded-lg border border-border bg-card p-4">
            <div className="flex-1">
              <p className="font-medium">Año {seq.year}</p>
              <p className="text-sm text-muted-foreground">
                Último recibo emitido: <span className="font-mono font-semibold">N° {String(seq.last_number).padStart(6, '0')}</span>
              </p>
              <p className="text-xs text-muted-foreground">
                Actualizado: {new Date(seq.updated_at).toLocaleDateString('es-BO')}
              </p>
            </div>

            {editing?.year === seq.year ? (
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min="0"
                  value={editing.value}
                  onChange={(e) => setEditing({ ...editing, value: e.target.value })}
                  className="w-28 rounded-md border border-input bg-background px-2 py-1.5 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                />
                <button
                  onClick={handleReset}
                  disabled={reset.isPending}
                  className="flex items-center gap-1 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
                >
                  {reset.isPending ? <Loader2 className="size-3 animate-spin" /> : <Check className="size-3" />}
                  Guardar
                </button>
                <button onClick={() => setEditing(null)} className="rounded-md border border-border px-3 py-1.5 text-sm hover:bg-muted">
                  Cancelar
                </button>
              </div>
            ) : (
              <button
                onClick={() => setEditing({ year: seq.year, value: String(seq.last_number) })}
                className="flex items-center gap-2 rounded-md border border-border px-3 py-2 text-sm hover:bg-muted"
              >
                <RefreshCw className="size-4" />
                Ajustar
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function Check({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
    </svg>
  );
}
